<template>
  <div v-if="showSpinner" class="credit-cards">
    <LoadingSpinner />
  </div>
  <div v-else>
    <div class="subscription-information">
      <div v-if="pageState === subscriptionPageState.default" class="content">
        <PrimaryEmailVerificationWrapperComponent
          notification-context="SubscriptionInformationComponent"
          :email="email"
        />
        <section>
          <div>
            <h1>{{ tn("subscriptionsHeading") }}</h1>
          </div>
          <div v-if="subscriptions.length > 0" class="table-to-list-wrapper">
            <table>
              <thead>
                <th class="sortable" :class="sortDirection.sku" @click="sortSubscriptions('sku')">
                  {{ tn("productCol") }}
                </th>
                <th class="sortable" :class="sortDirection.cardType" @click="sortSubscriptions('cardType')">
                  {{ tn("paymentMethodCol") }}
                </th>
                <th
                  class="sortable"
                  :class="sortDirection.customExpirationDate"
                  @click="sortSubscriptions('customExpirationDate')"
                >
                  {{ tn("nextPaymentDateCol") }}
                </th>
                <th>{{ tn("manageCol") }}</th>
              </thead>
              <tbody>
                <tr v-for="(s, i) in subscriptions" :key="i" :class="addClassToRow(s)">
                  <td :data-th="tn('productCol')">
                    <ProductColumnComponent :sub="s" @buy-safe-sub="buyNowSafe" />
                  </td>
                  <td :data-th="tn('paymentMethodCol')">
                    <PaymentMethodColumnComponent
                      :sub="s"
                      @subscription-page-state-change="setPageState"
                      @set-current-subscription="setCurrentSubscription"
                    />
                  </td>
                  <td :data-th="tn('nextPaymentDateCol')">
                    <NextPaymentColumnComponent :sub="s" />
                  </td>
                  <td class="no-marker">
                    <ManageSubscriptionColumnComponent
                      :sub="s"
                      :display-forced-auto-renew-modal="!!s.computer && s.computer.id === computerIdToAutoRenew"
                      @set-current-subscription="showAutoRenewFailed"
                      @move-payment-method-mr-to-ar="handleMoveFromMRtoAR"
                      @cancel-subscription-state="setPageState"
                      @change-payment-method="handleChangePaymentMethod"
                      @renew-safe="renewSafe"
                      @buy-safe-sub="buyNowSafe"
                      @auto-renew-updated="showAutoRenewSuccess"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="message-box">
            {{ tn("noDataToDisplay") }}
          </div>
        </section>

        <section v-if="isOutstandingInvoice">
          <OutstandingInvoiceAlertComponent
            :subscriptions="outStandingSubscriptions"
            @renew-safe="renewSafe"
          ></OutstandingInvoiceAlertComponent>
        </section>

        <section>
          <div>
            <h1>{{ tn("accountServicesHeading") }}</h1>
          </div>
          <div>
            <ul>
              <li class="mb">
                <button id="btnApplyAC" class="btn-link" @click="toggleApplyActivationCodeModal('click')">
                  {{ tn("applyACLink") }}
                </button>
              </li>
              <li v-if="vatInfo?.isVatSubscriber && vatInfoChanged">
                <div v-if="vatInfo.vatNumber !== null">
                  <div>{{ tn("vatIdNumber") }}: {{ vatInfo.vatNumber }}</div>
                  <button id="btnAddVat" class="btn-link" @click="toggleAddVatNumberModal('edit')">
                    {{ tn("edit") }}
                  </button>
                  -
                  <button id="btnRemoveVat" class="btn-link" :disabled="isRemovingVat" @click="removeVatNumber()">
                    <spinner :is-spinning="isRemovingVat" />
                    {{ t("Common.remove") }}
                  </button>
                </div>
                <button v-else id="btnVatIdNumber" class="btn-link" @click="toggleAddVatNumberModal('vatIdNumber')">
                  {{ tn("vatIdNumber") }}
                </button>
              </li>
            </ul>
          </div>
        </section>
      </div>
      <div v-if="pageState === subscriptionPageState.paymentMethodDetails" class="content">
        <h1>
          <button class="inline-btn-link" @click="pageState = subscriptionPageState.default">
            <svg-icon-component icon="chevron-left" class="icon-sm" />
          </button>
          {{ tn("paymentDetails") }}
        </h1>
        <div>
          <p>{{ tn("paymentDetailsHeader") }}</p>
          <SafeProductsInfoComponent :subscriptions="subscriptions" />
        </div>
        <div v-if="defaultCreditCard" class="card-credit">
          <CardCreditComponent :card="defaultCreditCard" />
        </div>
        <div>
          <button
            type="button"
            class="btn-primary"
            @click="setPageStateFromPaymentDetails('addPaymentMethod', 'paymentMethodDetails')"
          >
            {{ tn("changePaymentMethod") }}
          </button>
        </div>
        <i18n-t keypath="SubscriptionInformationComponent.note1" tag="p" class="mt" scope="global">
          <template #change>
            <strong>{{ tn("changePaymentMethod") }}</strong>
          </template>
        </i18n-t>
      </div>
      <div
        v-if="
          pageState === subscriptionPageState.addPaymentMethod ||
          pageState === subscriptionPageState.movePaymentMethodMRToAR
        "
        class="content"
      >
        <NotificationsComponent context="SubscriptionInformationComponent" />
        <h1>
          <button class="inline-btn-link" @click="setPageState(addPaymentPreviousState)">
            <svg-icon-component icon="chevron-left" class="icon-sm" />
          </button>
          {{ tn("addPaymentMethod") }}
        </h1>
        <div>
          <i18n-t keypath="SubscriptionInformationComponent.addPaymentHeader" tag="p" class="mt" scope="global">
            <template #all>
              <strong>{{ tn("all") }}</strong>
            </template>
          </i18n-t>
          <SafeProductsInfoComponent :subscriptions="subscriptions" />
        </div>
        <div>
          <AddCreditCardComponent
            :sub="currentSubscription"
            :credit-card-previous-state="addPaymentPreviousState"
            @subscription-page-state-change="setPageState"
            @payment-method-added="addPaymentMethodHandler"
          />
        </div>
      </div>
      <InvoiceDetailsComponent
        v-if="pageState === subscriptionPageState.invoiceDetails"
        :credit-cards="creditCards"
        :subscriptions="subscriptions"
        @success="handleSuccess"
        @subscription-page-state-change="setPageState"
        @updated-credit-cards="updateCreditCards"
      ></InvoiceDetailsComponent>
    </div>
    <AddVatNumberComponent
      :show-dialog="showAddVatNumberModal"
      :vat-info="vatInfo"
      @close-modal="toggleAddVatNumberModal"
      @save-modal="addVatNumber"
    />
    <ApplyActivationCodeComponent
      :show-dialog="showApplyActivationCodeModal"
      @close-modal="toggleApplyActivationCodeModal"
      @save-modal="saveApplyActivationCodeModal"
    />
  </div>
  <BestBuySupportSectionComponent v-if="hasBestBuySub" />
  <LoadingBoxComponent
    :show-dialog="buyFlowStore.showProcessingOrderMessage"
    :message="tn('changePaymentProcessingMessage')"
  />
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { logEvent, logException } from "@/common/logger";
import i18n, { t } from "@/i18n";
import { unifiedApi } from "@/common";
import {
  ISubscription,
  ICreditCard,
  IAutoRenewUpdateRequest,
  ISubscriberVatInfo,
  IActivationCodeDialogResponse,
  IGetCreditCardsWithoutPIIResponse,
} from "@/common/api/unifiedPortal/interfaces";
import { useUserStore } from "@/stores/user";
import { useNotificationsStore } from "@/stores/notifications";
import { SafeDataPurgeDays, INVOICE_LAST_OUTSTANDING_DAY, INVOICE_FIRST_OUTSTANDING_DAY, FLAGS } from "@/define";
import { pageState as subscriptionPageState, ExpirationState, KeyCodeSeller } from "@/globalEnums";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { getProductDisplayNameBySku, IProductDisplayRequest } from "@/common/productsBySku";
import {
  isSafeSubscriptionClosed,
  showWarning,
  showDanger,
  isProcessingRenewal,
  isNotActiveYet,
  processSubscriptionDataForDisplay,
} from "@/common/subscriptionHelper";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { useSiteSettingsStore } from "@/stores/siteSettings";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import Spinner from "@/components/shared/Spinner.vue";
import LoadingBoxComponent from "@/components/shared/LoadingBox.vue";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import ManageSubscriptionColumnComponent from "@/components/Subscriptions/ManageSubscriptionColumn.vue";
import NotificationsComponent from "@/components/shared/Notifications.vue";
import ProductColumnComponent from "@/components/Subscriptions/ProductColumn.vue";
import NextPaymentColumnComponent from "@/components/Subscriptions/NextPaymentColumn.vue";
import PaymentMethodColumnComponent from "@/components/Subscriptions/PaymentMethodColumn.vue";
import SafeProductsInfoComponent from "@/components/Subscriptions/SafeProductsInfo.vue";
import OutstandingInvoiceAlertComponent from "@/components/Subscriptions/OutstandingInvoiceAlert.vue";
import CardCreditComponent from "@/components/CreditCards/CardCredit.vue";
import AddCreditCardComponent from "@/components/CreditCards/AddCreditCard.vue";
import InvoiceDetailsComponent from "@/components/Subscriptions/InvoiceDetails.vue";
import BestBuySupportSectionComponent from "@/components/shared/BestBuySupportSection.vue";
import AddVatNumberComponent from "@/components/shared/dialogs/AddVatNumber/AddVatNumber.vue";
import ApplyActivationCodeComponent from "@/components/shared/dialogs/ApplyActivationCode/ApplyActivationCode.vue";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import PrimaryEmailVerificationWrapperComponent from "@/components/shared/PrimaryEmailVerificationWrapper.vue";
import { clearRefreshFunctions } from "@/components/shared/LanguageSelector/LanguageSelector";
import { getUserDetailsFromStore } from "@/common/userDetails";
import { getUrl } from "@/common/getUrl";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { pageStates } from "@/components/Buy/BuyEnums";

const componentName = "SubscriptionInformationComponent";

export default defineComponent({
  name: componentName,

  components: {
    NotificationsComponent,
    ManageSubscriptionColumnComponent,
    ProductColumnComponent,
    PaymentMethodColumnComponent,
    NextPaymentColumnComponent,
    LoadingBoxComponent,
    LoadingSpinner,
    OutstandingInvoiceAlertComponent,
    CardCreditComponent,
    AddCreditCardComponent,
    SafeProductsInfoComponent,
    InvoiceDetailsComponent,
    BestBuySupportSectionComponent,
    AddVatNumberComponent,
    ApplyActivationCodeComponent,
    Spinner,
    SvgIconComponent,
    PrimaryEmailVerificationWrapperComponent,
  },

  setup() {
    logEvent("created", componentName);

    const userStore = useUserStore();
    const buyFlowStore = useBuyFlowStore();
    const notificationsStore = useNotificationsStore();
    const subscriptionsStore = useSubscriptionsStore();
    const siteSettingsStore = useSiteSettingsStore();

    const isRemovingVat = ref<boolean>(false);
    const subscriptions = ref<ISubscription[]>([]);
    const outStandingSubscriptions = ref<ISubscription[]>([]);
    const showSpinner = ref<boolean>(true);
    const pageState = ref<subscriptionPageState>(subscriptionPageState.default);
    const creditCards = ref<ICreditCard[]>([]);
    const addPaymentPreviousState = ref<subscriptionPageState>(subscriptionPageState.default);
    const defaultCreditCard = ref<ICreditCard | null>(null);
    const refreshOutstandingInvoice = ref<boolean>(false);
    const currentSubscription = ref<ISubscription>();
    const vatInfo = ref<ISubscriberVatInfo>();
    const vatInfoChanged = ref<boolean>(true);
    const computerIdToAutoRenew = ref<number>(-1);
    const user = computed(() => userStore.currentUser);
    const router = useRouter();
    const showAddVatNumberModal = ref<boolean>(false);
    const showApplyActivationCodeModal = ref<boolean>(false);
    const activationCodes = ref<string[]>([]);
    const sortColumn = ref<string>("");
    const email = ref<string>("");
    const sortDirection = ref<Record<string, unknown>>({
      sku: "ascend",
      cardType: "",
      customExpirationDate: "",
    });

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    function toggleAddVatNumberModal(type: string | null) {
      type = type || "modal";
      logEvent(
        "toggleAddVatNumberModal",
        componentName,
        `${showAddVatNumberModal.value ? "Hiding" : "Showing"} via ${type}`
      );
      return (showAddVatNumberModal.value = !showAddVatNumberModal.value);
    }

    function toggleApplyActivationCodeModal(type: string | null) {
      type = type || "modal";
      logEvent(
        "toggleApplyActivationCodeModal",
        componentName,
        `${showApplyActivationCodeModal.value ? "Hiding" : "Showing"} via ${type}`
      );
      return (showApplyActivationCodeModal.value = !showApplyActivationCodeModal.value);
    }

    const isOutstandingInvoice = computed(() => {
      getOutstandingSubscriptions();
      return outStandingSubscriptions.value.length > 0;
    });

    function getOutstandingSubscriptions() {
      outStandingSubscriptions.value = [];
      subscriptions.value.forEach(sub => {
        if (
          sub.hasOutstandingInvoices &&
          sub.outstandingInvoice &&
          sub.outstandingInvoice.outstandingInvoiceDays <= INVOICE_FIRST_OUTSTANDING_DAY &&
          sub.outstandingInvoice.outstandingInvoiceDays >= INVOICE_LAST_OUTSTANDING_DAY
        ) {
          outStandingSubscriptions.value.push(sub);
        }
      });
    }

    const hasBestBuySub = computed(() => {
      return !!user.value?.subscriberInfo?.hasBestBuyS2;
    });

    notificationsStore.clearNotifications();

    onMounted(async () => {
      logEvent("mounted", componentName);
      email.value = user.value?.email ?? "";

      if (user.value?.subscriberInfo?.hasPendingWebrootLicenses && !user.value?.subscriberInfo?.hasValidatedEmail) {
        notificationsStore.addNotification({ type: "PrimaryEmailVerificationWebroot" });
      }
      try {
        await Promise.all([getSubscriptionsForDisplay(), setCreditCards(), getConsumerVatInfo()]);

        showSpinner.value = false;
        manageOutstandingInvoiceUrl();
      } catch (error) {
        handleApiError(error as AxiosError, false);
      }
    });

    onUnmounted(() => {
      clearRefreshFunctions();
    });

    async function getSubscriptionsForDisplay() {
      subscriptions.value = processData(await subscriptionsStore.getSubscriptions());
      sortSubscriptions("sku");
      processNotifications();
    }

    async function setCreditCards() {
      logEvent("setCreditCards", componentName);
      try {
        const response: IGetCreditCardsWithoutPIIResponse = (await unifiedApi.getUserCreditCardsWithoutPII()).data;
        creditCards.value = response.creditCards;
        defaultCreditCard.value =
          creditCards.value !== null && creditCards.value.length > 0
            ? creditCards.value.find(card => card.defaultPaymentMethod) ?? null
            : null;
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function showOutstandingInvoiceSection(subscription: ISubscription) {
      return (
        subscription.hasOutstandingInvoices &&
        subscription.outstandingInvoice &&
        subscription.outstandingInvoice.outstandingInvoiceDays <= INVOICE_FIRST_OUTSTANDING_DAY &&
        subscription.outstandingInvoice.outstandingInvoiceDays >= INVOICE_LAST_OUTSTANDING_DAY
      );
    }

    function buyNowSafe(subscription: ISubscription, sku: string | undefined = undefined) {
      if (showOutstandingInvoiceSection(subscription)) {
        setPageState("invoiceDetails");
        return;
      }

      if (FLAGS.ENABLE_BUYFLOW === "true") {
        buySafe(subscription, sku);
        return;
      }

      let redirectUrl = `${getUrl("CARBONITE_BUY_URL")}/selectplan?deviceId=${subscription.computer?.id}&personId=${user
        .value?.personId}`;
      if (!!sku) {
        redirectUrl = `${redirectUrl}&sku=${sku}`;
      }

      window.location.href = redirectUrl;
    }

    //Used for trials and upgrades. Goes to new VD buyflow
    function buySafe(sub: ISubscription, sku?: string) {
      buyFlowStore.changingFromMonthly = sub.isEvergreenMonthly;

      router.push({
        name: "selectPlan",
        query: {
          sku: sku ?? "",
          deviceId: sub.computer?.id,
          subscriptionNumber: sub.zuoraSubscriptionNumber,
        },
      });
    }

    function renewSafe(sub: ISubscription) {
      if (showOutstandingInvoiceSection(sub)) {
        setPageState("invoiceDetails");
        return;
      }

      if (FLAGS.ENABLE_BUYFLOW === "true") {
        router.push({
          name: "buy",
          query: {
            step: pageStates.payment,
            deviceId: sub.computer?.id,
            subscriptionNumber: sub.zuoraSubscriptionNumber,
          },
        });
        return;
      }

      window.location.href = `${getUrl("CARBONITE_BUY_URL")}/paymentForm?deviceId=${sub.computer?.id}&sku=${
        sub.sku
      }&personId=${user.value?.personId}`;
    }

    function handleChangePaymentMethod() {
      setPageStateFromPaymentDetails("addPaymentMethod", "paymentMethodDetails");
    }

    function handleMoveFromMRtoAR(sub: ISubscription) {
      logEvent("page state change", subscriptionPageState.movePaymentMethodMRToAR);
      pageState.value = subscriptionPageState.movePaymentMethodMRToAR;
      setCurrentSubscription(sub);
    }

    async function addPaymentMethodHandler(sub: ISubscription) {
      logEvent("addPaymentMethodHandler", componentName);
      notificationsStore.clearNotifications();
      if (pageState.value === subscriptionPageState.movePaymentMethodMRToAR) {
        try {
          const updateAutoRenewOptionRequest: IAutoRenewUpdateRequest = {
            allowAutoRenew: !sub.isAutoRenewEnabled,
            surveyChoice: "",
            zuoraSubscriptionNumber: sub.zuoraSubscriptionNumber,
            surveyChoiceCode: null,
          };
          const updated = (await unifiedApi.updateAutoRenewOption(updateAutoRenewOptionRequest)).data;
          if (updated) {
            //If actions happen we need to force the store to refresh so we are't left with stale data
            subscriptions.value = processData(await subscriptionsStore.forceRefreshAndGetSubscriptions());

            const request: IProductDisplayRequest = {
              sku: sub.sku,
              webrootLicenseKey: sub.keyCode ?? undefined,
              isTrial: sub.isTrial,
            };

            const productDisplayName = i18n.global.t(getProductDisplayNameBySku(request)).toString();

            notificationsStore.addNotification({
              type: "MovePaymentMethodMRToAR",
              params: { plan: productDisplayName },
            });
          } else {
            notificationsStore.addNotification({ type: "AutoRenewTurnOnOrOffFailure" });
          }
        } catch (error) {
          buyFlowStore.showProcessingOrderMessage = false;
          logException(error as Error);
          notificationsStore.addNotification({ type: "AutoRenewTurnOnOrOffFailure" });
        }
      } else {
        //If actions happen we need to force the store to refresh so we are't left with stale data
        subscriptions.value = processData(await subscriptionsStore.forceRefreshAndGetSubscriptions());
        notificationsStore.addNotification({ type: "AddCreditCardSuccess" });
        //after success , update the vat info section as well
        await getConsumerVatInfo();
      }
      buyFlowStore.showProcessingOrderMessage = false;
      pageState.value = subscriptionPageState.default;
      logEvent("page state change", subscriptionPageState.default);
      setCreditCards();
    }

    async function setPageState(state: string) {
      notificationsStore.clearNotifications();
      pageState.value = subscriptionPageState[state];
      logEvent("page state change", subscriptionPageState[state]);
      //if coming back to default page then show banners just like at the start of page.
      if (state === subscriptionPageState.default) {
        processNotifications();
      } else if (state === subscriptionPageState.cancelSubscriptionFailure) {
        notificationsStore.addNotification({ type: "CancelSubscriptionFailure" });
        pageState.value = subscriptionPageState.default;
        logEvent("page state change", subscriptionPageState.default);
      } else if (state === subscriptionPageState.cancelSubscriptionSuccess) {
        logEvent("cancelSubscriptionSuccess");
        showSpinner.value = true;
        try {
          //If actions happen we need to force the store to refresh so we are't left with stale data
          subscriptions.value = processData(await subscriptionsStore.forceRefreshAndGetSubscriptions());
          showSpinner.value = false;
          notificationsStore.addNotification({ type: "CancelSubscriptionSuccess" });
          pageState.value = subscriptionPageState.default;
          logEvent("page state change", subscriptionPageState.default);
        } catch (err) {
          logException(err as Error);
        }
      }
    }

    async function showAutoRenewSuccess(sub: ISubscription) {
      logEvent("showAutoRenewSuccess", componentName);
      notificationsStore.clearNotifications();
      try {
        showSpinner.value = true;
        //If actions happen we need to force the store to refresh so we are't left with stale data
        subscriptions.value = processData(await subscriptionsStore.forceRefreshAndGetSubscriptions());
        showSpinner.value = false;

        const request: IProductDisplayRequest = {
          sku: sub.sku,
          webrootLicenseKey: sub.keyCode ?? undefined,
          isTrial: sub.isTrial,
        };

        const productDisplayName = i18n.global.t(getProductDisplayNameBySku(request)).toString();
        if (!sub.isAutoRenewEnabled) {
          notificationsStore.addNotification({
            type: "AutoRenewTurnOnSuccess",
            params: { carbonitePlan: productDisplayName },
          });
        } else {
          notificationsStore.addNotification({
            type: "AutoRenewTurnOffSuccess",
            params: { carbonitePlan: productDisplayName },
          });
        }
      } catch (err) {
        logException(err as Error);
      }
    }

    function showAutoRenewFailed() {
      logEvent("AutoRenewTurnOnOrOffFailure");
      notificationsStore.clearNotifications();
      notificationsStore.addNotification({ type: "AutoRenewTurnOnOrOffFailure" });
    }

    function setAddPaymentPreviousState(state: string) {
      addPaymentPreviousState.value = subscriptionPageState[state];
    }

    function setPageStateFromPaymentDetails(state: string, previousState: string) {
      setPageState(state);
      setAddPaymentPreviousState(previousState);
    }

    function addClassToRow(subscription: ISubscription): string {
      let className = "";
      if (showDanger(subscription)) {
        className = "row-danger";
      } else if (showWarning(subscription)) {
        className = "row-warning";
      }

      return className;
    }

    /**
     * Check all the subscriptions for subs that need a notification
     */
    function processNotifications() {
      if (subscriptions.value.length === 0) {
        return;
      }
      //We only want to show the highest priority notifications

      //Amazon Webroot, Only show banner for first 30 days of expiration
      if (
        subscriptions.value.some(
          l => l.keyCodeSellerType === KeyCodeSeller.Amazon && l.expiration === ExpirationState.Expired
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Best Buy S2/TTS Webroot subs, Only show banner for first 30 days of expiration
      if (
        subscriptions.value.some(
          l => l.keyCodeSellerType === KeyCodeSeller.BestBuy && l.expiration === ExpirationState.Expired
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //outstanding Invoice
      if (isOutstandingInvoice.value) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Evergreen Monthly
      if (
        subscriptions.value.some(l => l.isEvergreenMonthly && l.expirationDays <= 0 && !isSafeSubscriptionClosed(l))
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Direct Expired Banner
      if (
        subscriptions.value.some(
          l =>
            (!l.resellerId || l.isPartnerReferral) &&
            !l.isEvergreenMonthly &&
            !l.isTrial &&
            ((!l.isAutoRenewEnabled && l.expiration === ExpirationState.Expired) ||
              (l.isAutoRenewEnabled &&
                !isProcessingRenewal(l) &&
                l.expirationDays < 0 &&
                l.expirationDays > SafeDataPurgeDays.Reseller) ||
              (l.computer?.remainingDaysToPurge &&
                l.computer.remainingDaysToPurge > 0 &&
                !l.computer?.dataHasBeenPurged))
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Direct Trial Expired Banner
      if (
        subscriptions.value.some(
          l =>
            (!l.resellerId || l.isPartnerReferral) &&
            l.isTrial &&
            l.expirationDays < 0 &&
            l.expirationDays > SafeDataPurgeDays.Trials
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Reseller Expired Banner at -29 days switch to direct message
      if (
        subscriptions.value.some(
          l =>
            l.resellerId &&
            l.resellerId > 0 &&
            !l.isPartnerReferral &&
            !l.isTrial &&
            ((l.expirationDays <= -29 && l.expirationDays > SafeDataPurgeDays.Reseller) ||
              (l.computer?.remainingDaysToPurge &&
                l.computer.remainingDaysToPurge > 0 &&
                !l.computer?.dataHasBeenPurged))
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Reseller Expired Banner
      if (
        subscriptions.value.some(
          l =>
            l.resellerId &&
            l.resellerId > 0 &&
            !l.isPartnerReferral &&
            !l.isTrial &&
            ((!l.isAutoRenewEnabled && l.expiration === ExpirationState.Expired) ||
              (l.isAutoRenewEnabled &&
                !isProcessingRenewal(l) &&
                l.expirationDays < 0 &&
                l.expirationDays > SafeDataPurgeDays.Reseller) ||
              (l.computer?.remainingDaysToPurge &&
                l.computer.remainingDaysToPurge > 0 &&
                !l.computer?.dataHasBeenPurged))
        )
      ) {
        notificationsStore.addNotification({ type: "ResellerSubscriptionsExpired", dismissible: false });
        return;
      }

      //Webroot Expired
      if (
        subscriptions.value.some(
          l =>
            l.keyCodeSellerType === KeyCodeSeller.Webroot &&
            !isNotActiveYet(l) &&
            (l.expiration === ExpirationState.Expired || l.expiration === ExpirationState.FinalWarning)
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
        return;
      }

      //Reseller Trial Expired Banner
      if (
        subscriptions.value.some(
          l =>
            l.resellerId &&
            l.resellerId > 0 &&
            !l.isPartnerReferral &&
            l.isTrial &&
            l.expirationDays <= 0 &&
            l.expirationDays > SafeDataPurgeDays.Trials
        )
      ) {
        notificationsStore.addNotification({ type: "ResellerTrialSubscriptionsExpired", dismissible: false });
        return;
      }

      //Direct Expiring soon Banner
      if (
        subscriptions.value.some(
          l =>
            l.keyCodeSellerType !== KeyCodeSeller.BestBuy &&
            (!l.resellerId || l.isPartnerReferral) &&
            !l.isAutoRenewEnabled &&
            !l.isEvergreenMonthly &&
            !l.isTrial &&
            l.expiration === ExpirationState.Upcoming
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsAlmostExpired", dismissible: false });
        return;
      }

      //Reseller Expiring soon Banner
      if (
        subscriptions.value.some(
          l =>
            l.resellerId &&
            l.resellerId > 0 &&
            !l.isPartnerReferral &&
            !l.isTrial &&
            l.expiration === ExpirationState.Upcoming &&
            !l.isAutoRenewEnabled
        )
      ) {
        notificationsStore.addNotification({ type: "ResellerSubscriptionsAlmostExpired", dismissible: false });
        return;
      }

      //Direct Trial Expiring soon Banner
      if (
        subscriptions.value.some(
          l =>
            l.keyCodeSellerType !== KeyCodeSeller.BestBuy &&
            (!l.resellerId || l.isPartnerReferral) &&
            !l.isAutoRenewEnabled &&
            !l.isEvergreenMonthly &&
            l.isTrial &&
            l.expiration === ExpirationState.Upcoming
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionWillExpireTrial", dismissible: false });
        return;
      }

      //Reseller Trial Expiring soon Banner
      if (
        subscriptions.value.some(
          l =>
            l.resellerId &&
            l.resellerId > 0 &&
            !l.isPartnerReferral &&
            l.isTrial &&
            l.expiration === ExpirationState.Upcoming &&
            !l.isAutoRenewEnabled
        )
      ) {
        notificationsStore.addNotification({ type: "ResellerTrialSubscriptionsAlmostExpired", dismissible: false });
        return;
      }

      //Webroot expiring soon
      if (
        subscriptions.value.some(
          l =>
            l.keyCodeSellerType === KeyCodeSeller.Webroot &&
            !isNotActiveYet(l) &&
            l.expiration === ExpirationState.Upcoming &&
            !l.isAutoRenewEnabled
        )
      ) {
        notificationsStore.addNotification({ type: "SubscriptionsAlmostExpired", dismissible: false });
        return;
      }
    }

    /**
     * Add any post data retrieval processing for each sub
     */
    function processData(subscriptions: ISubscription[]): ISubscription[] {
      const data = processSubscriptionDataForDisplay(subscriptions);
      manageAutoRenewUrl();
      return data ?? null;
    }

    async function handleSuccess() {
      refreshOutstandingInvoice.value = false;
      notificationsStore.addNotification({ type: "RenewSuccessMessage", dismissible: false });
      pageState.value = subscriptionPageState.default;
      logEvent("page state change", subscriptionPageState.default);
      //If actions happen we need to force the store to refresh so we are't left with stale data
      subscriptions.value = processData(await subscriptionsStore.forceRefreshAndGetSubscriptions());
      refreshOutstandingInvoice.value = true;
    }

    async function saveApplyActivationCodeModal(response: IActivationCodeDialogResponse) {
      try {
        showSpinner.value = true;
        if (response?.type === "existingSub") {
          showSpinner.value = true;
          subscriptionsStore.forceRefresh();
          await getSubscriptionsForDisplay();
          notificationsStore.addNotification({ type: "ActivationCodeAppliedSuccess" });
        } else if (response?.type === "newSub") {
          //Applied to new computer, go to backup page to download the installer
          siteSettingsStore.activationCodeAppliedSuccess = true;
          siteSettingsStore.activationCodeToInstall = response?.activationCode;
          await getUserDetailsFromStore(true);
          router.push("/backup");
        }
        showSpinner.value = false;

        toggleApplyActivationCodeModal("saveApplyActivationCodeModal");
        await reloadSubscriberInfo();
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    async function updateCreditCards(updatedCreditCards: ICreditCard[]) {
      creditCards.value = updatedCreditCards;
    }

    async function getConsumerVatInfo() {
      try {
        logEvent("getConsumerVatInfo", componentName);
        vatInfo.value = (await unifiedApi.getConsumerVatInfoAsync(null)).data;
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    async function addVatNumber(response: string) {
      logEvent("addVatNumber", componentName);
      if (response === "OK") {
        //reload vat info data
        await getConsumerVatInfo();
        refreshVatInfoSection();
      } else {
        logEvent("Failure in adding Vat Number", JSON.stringify(response));
      }

      toggleAddVatNumberModal("addVatNumber");
    }

    async function removeVatNumber() {
      isRemovingVat.value = true;

      try {
        const vatInfoResponse = (await unifiedApi.setVatInfo("", "")).data;
        if (vatInfoResponse.success) {
          if (vatInfo.value) vatInfo.value.vatNumber = null;
          refreshVatInfoSection();

          //reload vat info data
          await getConsumerVatInfo();
        } else {
          logEvent("Failure in removing Vat Number", vatInfoResponse.responseMessage);
        }
      } catch (err) {
        logException(err as Error);
      }
      isRemovingVat.value = false;
    }

    function refreshVatInfoSection() {
      vatInfoChanged.value = false;
      setTimeout(() => {
        vatInfoChanged.value = true;
      }, 10);
    }

    function manageAutoRenewUrl() {
      //cSpell:ignore autorenew
      // for url /subscriptions/computerId=$computerId$&action=autorenew will open autorenew model for the matching computer/subscription
      if (router.currentRoute.value.query.computerId) {
        const computerId: number = parseInt(router.currentRoute.value.query.computerId.toString());
        if (computerId > 0 && router.currentRoute.value.query.action?.toString().toLowerCase() === "autorenew") {
          computerIdToAutoRenew.value = computerId;
        }
      }
    }

    function manageOutstandingInvoiceUrl() {
      //cSpell:ignore outstandinginvoice
      //if user has outstanding invoice then url /subscriptions?action=outstandinginvoice&paymentMethod=add will navigate directly to invoice details section
      if (
        isOutstandingInvoice.value &&
        router.currentRoute.value.query.action?.toString().toLowerCase() === "outstandinginvoice" &&
        router.currentRoute.value.query.paymentMethod?.toString().toLowerCase() === "add"
      ) {
        setPageState(subscriptionPageState.invoiceDetails);
      }
    }

    function setCurrentSubscription(sub: ISubscription) {
      currentSubscription.value = sub;
    }

    function sortSubscriptions(field: string) {
      //turn off sorting indicator on old column
      if (sortColumn.value !== field) sortDirection.value[sortColumn.value] = "";

      //pick a direction for the sort
      if (sortDirection.value[field] === "ascend") sortDirection.value[field] = "descend";
      else if (sortDirection.value[field] === "descend") sortDirection.value[field] = "ascend";
      else sortDirection.value[field] = "ascend";

      const order = sortDirection.value[field] === "ascend" ? -1 : 1;

      if (subscriptions.value)
        subscriptions.value = subscriptions.value.sort((a: ISubscription, b: ISubscription) => {
          if (a[field] > b[field]) return order;
          if (a[field] < b[field]) return -order;
          return 0;
        });

      //remember the column being sorted
      sortColumn.value = field;
    }

    const language = computed(() => userStore.selectedLanguage);
    watch(language, async () => {
      await getSubscriptionsForDisplay();
    });

    //Expose to template
    return {
      tn,
      pageState,
      showSpinner,
      subscriptions,
      outStandingSubscriptions,
      addClassToRow,
      buyFlowStore,
      buyNowSafe,
      setPageState,
      showAutoRenewFailed,
      handleMoveFromMRtoAR,
      handleChangePaymentMethod,
      renewSafe,
      showAutoRenewSuccess,
      refreshOutstandingInvoice,
      vatInfo,
      vatInfoChanged,
      addVatNumber,
      removeVatNumber,
      subscriptionPageState,
      defaultCreditCard,
      setPageStateFromPaymentDetails,
      addPaymentPreviousState,
      currentSubscription,
      handleSuccess,
      creditCards,
      hasBestBuySub,
      isOutstandingInvoice,
      addPaymentMethodHandler,
      updateCreditCards,
      computerIdToAutoRenew,
      setCurrentSubscription,
      showAddVatNumberModal,
      toggleAddVatNumberModal,
      showApplyActivationCodeModal,
      toggleApplyActivationCodeModal,
      saveApplyActivationCodeModal,
      activationCodes,
      isRemovingVat,
      sortSubscriptions,
      sortDirection,
      sortColumn,
      email,
      t,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";

.best-buy-section {
  margin-top: 20px;
}
</style>
